import type React from 'react'
import type { ReactNode } from 'react'
import { ChevRightIcon } from '../icons'
import { cn, cva, type VariantProps } from '../lib/cva'
import { Text } from './Text'

const linkVariants = cva('no-underline inline-block', {
	variants: {
		disabled: {
			true: 'text-primary-300 cursor-default',
			false: 'text-primary-600 cursor-pointer hover:text-primary-800',
		},
		size: {
			medium: 'text-base',
			small: 'text-sm',
		},
		variant: {
			default: '',
			breadcrumb: '',
		},
	},
	compoundVariants: [
		{
			variant: 'breadcrumb',
			class: 'text-neutral-800 hover:text-neutral-800',
		},
	],
	defaultVariants: {
		disabled: false,
		size: 'medium',
		variant: 'default',
	},
})

const chevronVariants = cva('inline-block', {
	variants: {
		disabled: {
			true: 'fill-primary-300 hover:fill-primary-300',
			false: 'fill-primary-600 hover:fill-primary-800',
		},
		variant: {
			default: '',
			breadcrumb: '',
		},
	},
	compoundVariants: [
		{
			variant: 'breadcrumb',
			disabled: false,
			class: 'fill-neutral-300 hover:fill-neutral-300',
		},
	],
	defaultVariants: {
		disabled: false,
		variant: 'default',
	},
})

interface LinkProps extends VariantProps<typeof linkVariants> {
	children: ReactNode
	href?: string
	withChevron?: boolean
	className?: string
	size?: 'medium' | 'small'
	variant?: 'default' | 'breadcrumb'
	onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}

export const Link: React.FC<LinkProps> = ({
	children,
	href,
	disabled = false,
	withChevron = false,
	className,
	size = 'medium',
	variant = 'default',
	onClick,
}) => {
	const iconSize = size === 'medium' ? 18 : 16

	if (disabled) {
		return (
			<Text className={cn(linkVariants({ disabled, size, variant }), className)}>
				<Text className='inline-block'>{children}</Text>
				{withChevron && <ChevRightIcon size={iconSize} className={chevronVariants({ disabled, variant })} />}
			</Text>
		)
	} else {
		return (
			<a href={href} className={cn('group', linkVariants({ disabled, size, variant }), className)} onClick={onClick}>
				<Text className='inline-block group-hover:underline'>{children}</Text>
				{withChevron && <ChevRightIcon size={iconSize} className={chevronVariants({ disabled, variant })} />}
			</a>
		)
	}
}
